import * as THREE from 'three';

export class ThreeJSManager {
  constructor() {
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.objects = new Map();
    this.animations = new Map();
    this.isInitialized = false;
  }

  async init() {
    this.setupScene();
    this.setupCamera();
    this.setupRenderer();
    this.setupLights();
    this.setupEventListeners();
    this.animate();
    this.isInitialized = true;
  }

  setupScene() {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x000000);
  }

  setupCamera() {
    this.camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    this.camera.position.z = 5;
  }

  setupRenderer() {
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    document.body.appendChild(this.renderer.domElement);
  }

  setupLights() {
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    this.scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(0, 1, 1);
    this.scene.add(directionalLight);
  }

  setupEventListeners() {
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  handleResize() {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  animate() {
    requestAnimationFrame(this.animate.bind(this));
    
    this.animations.forEach(animation => {
      if (animation.isActive) {
        animation.update();
      }
    });

    this.renderer.render(this.scene, this.scene);
  }

  createCube(options = {}) {
    const geometry = new THREE.BoxGeometry(
      options.width || 1,
      options.height || 1,
      options.depth || 1
    );
    
    const material = new THREE.MeshLambertMaterial({
      color: options.color || 0x00ff00
    });
    
    const cube = new THREE.Mesh(geometry, material);
    this.scene.add(cube);
    
    const id = `cube_${Date.now()}`;
    this.objects.set(id, cube);
    
    return id;
  }

  createSphere(options = {}) {
    const geometry = new THREE.SphereGeometry(
      options.radius || 0.5,
      options.widthSegments || 32,
      options.heightSegments || 16
    );
    
    const material = new THREE.MeshLambertMaterial({
      color: options.color || 0xff0000
    });
    
    const sphere = new THREE.Mesh(geometry, material);
    this.scene.add(sphere);
    
    const id = `sphere_${Date.now()}`;
    this.objects.set(id, sphere);
    
    return id;
  }

  createParticleSystem(options = {}) {
    const particleCount = options.count || 1000;
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    
    for (let i = 0; i < particleCount * 3; i += 3) {
      positions[i] = (Math.random() - 0.5) * 10;
      positions[i + 1] = (Math.random() - 0.5) * 10;
      positions[i + 2] = (Math.random() - 0.5) * 10;
    }
    
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    
    const material = new THREE.PointsMaterial({
      color: options.color || 0xffffff,
      size: options.size || 0.05
    });
    
    const particles = new THREE.Points(geometry, material);
    this.scene.add(particles);
    
    const id = `particles_${Date.now()}`;
    this.objects.set(id, particles);
    
    return id;
  }

  animateObject(id, animation) {
    const object = this.objects.get(id);
    if (!object) return;

    const anim = {
      object,
      isActive: true,
      update: animation.bind(null, object)
    };

    this.animations.set(id, anim);
  }

  rotateObject(id, speed = 0.01) {
    this.animateObject(id, (object) => {
      object.rotation.x += speed;
      object.rotation.y += speed;
    });
  }

  moveObject(id, direction = 'up', speed = 0.01) {
    this.animateObject(id, (object) => {
      switch (direction) {
        case 'up':
          object.position.y += speed;
          break;
        case 'down':
          object.position.y -= speed;
          break;
        case 'left':
          object.position.x -= speed;
          break;
        case 'right':
          object.position.x += speed;
          break;
      }
    });
  }

  destroy() {
    window.removeEventListener('resize', this.handleResize);
    
    if (this.renderer) {
      document.body.removeChild(this.renderer.domElement);
      this.renderer.dispose();
    }
    
    this.objects.clear();
    this.animations.clear();
  }
} 